import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import Chart from "react-apexcharts";
import { Grid, Segment, Button, Form, Loader, Input, Dropdown, Popup, Icon, Checkbox, Table, Modal, List, Label } from "semantic-ui-react"
import { useUnsavedChangesReporter } from "../../../hooks/useUnsavedChangesGuard";
import { getSafeSelectedWorkspace } from "../../../utils/mappers/user";
import { ANALYTICS_CHART_LINE_OPTIONS } from "../AnalyticsSection/utils";
import { getDemandData, getWorkspaceMaterialsList, getDemandDataDownload, getIgnoredDates, patchIgnoredDates, getOrderCalculationPreview } from "../../../actions/analytics-v2";
import { getTodayFormatted, get12MonthsAgoFormatted, get12MonthsAheadFormatted, toExcelNumberFormatWithDecimals } from "../../../utils/format";
import { mapCurrentUserToWorkspaceOptions } from "../../../utils/mappers/workspace";
import _ from 'lodash'
// import "../AnalyticsSection/AnalyticsSection.css"
import "./Demand.css"

const pad2 = (n) => String(n).padStart(2, "0");

const dateKeyFromDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const formatSelectionDate = (timestamp) => {
    const d = new Date(timestamp);
    return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const startOfDayTimestamp = (dateKey) => new Date(`${dateKey}T00:00:00`).getTime();
const endOfDayTimestamp = (dateKey) => new Date(`${dateKey}T23:59:59.999`).getTime();

const sameDateSet = (a, b) => {
    if (a.length !== b.length) return false;
    const setA = new Set(a);
    return b.every((d) => setA.has(d));
};

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const HALF_DAY_MS = ONE_DAY_MS / 2;
// Matches chartOptions.stroke.width below — kept as one constant since both need to agree.
const BAR_STROKE_WIDTH_PX = 2;

const floorToDayTimestamp = (ts) => {
    const d = new Date(ts);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
};

// Groups consecutive calendar days into single {start, end} ranges so a wide, contiguous
// selection becomes one annotation instead of one per date (hundreds of individual SVG
// annotations for a wide drag is what was making the chart hang).
const buildMergedDateRanges = (dateKeys) => {
    if (dateKeys.length === 0) return [];
    const sorted = [...dateKeys].sort();
    const ranges = [{ start: sorted[0], end: sorted[0] }];

    for (let i = 1; i < sorted.length; i++) {
        const lastRange = ranges[ranges.length - 1];
        const isConsecutiveDay = startOfDayTimestamp(sorted[i]) - startOfDayTimestamp(lastRange.end) === ONE_DAY_MS;
        if (isConsecutiveDay) {
            lastRange.end = sorted[i];
        } else {
            ranges.push({ start: sorted[i], end: sorted[i] });
        }
    }
    return ranges;
};

const isoDateKeyToDDMMYYYY = (isoDateKey) => {
    const [year, month, day] = isoDateKey.split("-");
    return `${day}/${month}/${year}`;
};

const ddmmyyyyToIsoDateKey = (ddmmyyyy) => {
    const [day, month, year] = ddmmyyyy.split("/");
    return `${year}-${pad2(month)}-${pad2(day)}`;
};

const expandDateRangeToKeys = (startKey, endKey) => {
    const keys = [];
    for (let d = new Date(`${startKey}T00:00:00`); d <= new Date(`${endKey}T00:00:00`); d.setDate(d.getDate() + 1)) {
        keys.push(dateKeyFromDate(d));
    }
    return keys;
};

const timestampFromClientX = (drag, clientX) => {
    const pixel = clientX - drag.gridLeft;
    const clamped = Math.max(0, Math.min(drag.gridWidth, pixel));
    return drag.domainMin + (clamped / drag.gridWidth) * (drag.domainMax - drag.domainMin);
};

const dayTimestampFromClientX = (drag, clientX) => floorToDayTimestamp(timestampFromClientX(drag, clientX));

const resolveHoveredDayFromDom = (wrapperEl, globals, clientX, testY) => {
    if (typeof document.elementsFromPoint !== "function") return null;
    const stack = document.elementsFromPoint(clientX, testY);
    for (let i = 0; i < stack.length; i++) {
        const el = stack[i];
        if (el.tagName !== "path" || !wrapperEl.contains(el)) continue;
        const seriesIndex = parseInt(el.getAttribute("index"), 10);
        const pointIndex = parseInt(el.getAttribute("j"), 10);
        if (Number.isNaN(seriesIndex) || Number.isNaN(pointIndex)) continue;
        const seriesX = globals.seriesX && globals.seriesX[seriesIndex];
        if (seriesX && pointIndex >= 0 && pointIndex < seriesX.length) {
            return floorToDayTimestamp(seriesX[pointIndex]);
        }
    }
    return null;
};

const resolveDayTimestamp = (wrapperEl, globals, drag, gridRect, clientX) => {
    if (clientX >= gridRect.left && clientX <= gridRect.right) {
        const gridHeight = gridRect.bottom - gridRect.top;
        const STEPS = 40;
        for (let s = 0; s <= STEPS; s++) {
            const testY = gridRect.bottom - (gridHeight * s) / STEPS;
            const fromDom = resolveHoveredDayFromDom(wrapperEl, globals, clientX, testY);
            if (fromDom !== null) return fromDom;
        }
    }
    return dayTimestampFromClientX(drag, clientX);
};

export const Demand = ({
    currentWorkspaceId,
    currentUser,
    workspaces,
    materialDataList,
    chartOneHeight,
    savedAggregateFromDate,
    savedAggregateToDate,
    workspaceLevelSettings,
    // Demand
}) => {
    const { t } = useTranslation();
    const [workspaceSelected, setWorkspaceSelected] = useState(currentWorkspaceId)
    const [materialCodeSelected, setMaterialCodeSelected] = useState("")
    const [materialList, setMaterialList] = useState(materialDataList)
    const [aggregateFromDate, setAggregateFromDate] = useState(savedAggregateFromDate || get12MonthsAgoFormatted());
    const [aggregateToDate, setAggregateToDate] = useState(savedAggregateToDate || getTodayFormatted());
    const [demandData, setDemandData] = useState([])
    const [demandDataForecast, setDemandDataForecast] = useState([])
    const [filterDemandData, setFilterDemandData] = useState({})
    const [includeForecast, setIncludeForecast] = useState(false)
    const [filterDemandDataForecast, setFilterDemandDataForecast] = useState({})
    const [chartData, setChartData] = useState([])
    const [fetchingData, setFetchingData] = useState(true)
    const [backendIgnoredDatesByMaterial, setBackendIgnoredDatesByMaterial] = useState({})
    const [pendingIgnoredDatesByMaterial, setPendingIgnoredDatesByMaterial] = useState({})
    const [savingIgnoredDates, setSavingIgnoredDates] = useState(false)
    const [isSelectionModeActive, setIsSelectionModeActive] = useState(false)
    const [isSelectedRangesSectionCollapsed, setIsSelectedRangesSectionCollapsed] = useState(true)
    const [editingRangeKey, setEditingRangeKey] = useState(null)
    const [editRangeFromDate, setEditRangeFromDate] = useState("")
    const [editRangeToDate, setEditRangeToDate] = useState("")
    const [currentCalculationResult, setCurrentCalculationResult] = useState(null)
    const [calculationPreviewResult, setCalculationPreviewResult] = useState(null)
    const [loadingCalculationPreview, setLoadingCalculationPreview] = useState(false)
    const [showEditedMaterialsModal, setShowEditedMaterialsModal] = useState(false)

    const materialOptions = useMemo(()=>{
        if(!_.isEmpty(materialList)){
            return materialList
                    .map(material=>{ 
                        let materialLocationCode = material.slice(material.lastIndexOf("(") + 1, -1)
                        return {
                            key: material,
                            value: materialLocationCode,
                            text: material,
                        }
                    })
        }
        return []
    }, [materialList])

    const decimalsNumber = _.get(workspaceLevelSettings, "decimals_number", 2);

    const materialCalculationParams = useMemo(() => {
        if (!currentCalculationResult) return null;
        const formatMetricValue = (value, precision) => (
            typeof value === "number" ? toExcelNumberFormatWithDecimals(value, undefined, precision) : value
        );
        const buildRow = (label, currentRaw, nextRaw, precision) => ({
            label,
            currentRaw,
            nextRaw,
            current: formatMetricValue(currentRaw, precision),
            next: nextRaw !== null ? formatMetricValue(nextRaw, precision) : null,
        });
        return [
            buildRow(
                t("ordersTable.columns.average_daily_usage"),
                currentCalculationResult.average_daily_usage,
                calculationPreviewResult ? calculationPreviewResult.average_daily_usage : null,
                decimalsNumber
            ),
            buildRow(
                t("ordersTable.columns.actual_order"),
                currentCalculationResult.actual_order,
                calculationPreviewResult ? calculationPreviewResult.actual_order : null,
                2
            ),
        ];
    }, [currentCalculationResult, calculationPreviewResult, decimalsNumber, t]);

    const backendIgnoredDates = backendIgnoredDatesByMaterial[materialCodeSelected] || [];
    const pendingIgnoredDates = pendingIgnoredDatesByMaterial[materialCodeSelected] || [];

    const updatePendingIgnoredDates = useCallback((updater) => {
        setPendingIgnoredDatesByMaterial((prev) => {
            const current = prev[materialCodeSelected] || [];
            const next = typeof updater === "function" ? updater(current) : updater;
            return { ...prev, [materialCodeSelected]: next };
        });
    }, [materialCodeSelected]);

    const chartInstanceRef = useRef(null);
    const chartWrapperRef = useRef(null);
    const selectionRectElRef = useRef(null);
    const selectionStartLabelRef = useRef(null);
    const selectionEndLabelRef = useRef(null);
    const hoverLabelElRef = useRef(null);
    const hoverLineElRef = useRef(null);
    const ignoredRangeRemoveBtnElRef = useRef(null);
    const ignoredRangeIndicatorElRef = useRef(null);
    const ignoredRangeFromLabelElRef = useRef(null);
    const ignoredRangeToLabelElRef = useRef(null);
    const dragStateRef = useRef(null);
    const hoveredIgnoredRangeRef = useRef(null);
    const [hoveredIgnoredRangeKey, setHoveredIgnoredRangeKey] = useState(null);
    // Bumped on mount/zoom/pan/resize so the stroke-width compensation below (which depends on the
    // grid's current pixel width and time domain) gets recomputed instead of going stale.
    const [chartViewVersion, setChartViewVersion] = useState(0);

    const handleChartMounted = useCallback((chartContext) => {
        chartInstanceRef.current = chartContext;
        setChartViewVersion((v) => v + 1);
    }, []);

    const bumpChartViewVersion = useCallback(() => {
        setChartViewVersion((v) => v + 1);
    }, []);

    useEffect(() => {
        window.addEventListener("resize", bumpChartViewVersion);
        return () => window.removeEventListener("resize", bumpChartViewVersion);
    }, [bumpChartViewVersion]);

    const mergedIgnoredRanges = useMemo(() => {
        const backendDatesSet = new Set(backendIgnoredDates);
        const savedDates = [];
        const newDates = [];
        pendingIgnoredDates.forEach((dateKey) => {
            (backendDatesSet.has(dateKey) ? savedDates : newDates).push(dateKey);
        });
        return [
            ...buildMergedDateRanges(savedDates).map((range) => ({ ...range, isNew: false })),
            ...buildMergedDateRanges(newDates).map((range) => ({ ...range, isNew: true })),
        ].sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));
    }, [pendingIgnoredDates, backendIgnoredDates]);

    useEffect(() => {
        const chart = chartInstanceRef.current;
        const gridEl = chartWrapperRef.current && chartWrapperRef.current.querySelector(".apexcharts-grid");
        const globals = chart && chart.w && chart.w.globals;
        if (!chart || !gridEl || !globals) return;

        if (_.isEmpty(demandData)) {
            chart.updateOptions({ annotations: { xaxis: [] } }, false, true, true, false);
            return;
        }

        const gridWidth = gridEl.getBoundingClientRect().width;
        const msPerPx = (globals.maxX - globals.minX) / gridWidth;
        const halfStrokeWidthMs = (BAR_STROKE_WIDTH_PX / 2) * msPerPx;

        chart.updateOptions({
            annotations: {
                xaxis: mergedIgnoredRanges.map(({ start, end, isNew }) => {
                    const isHovered = `${start}_${end}` === hoveredIgnoredRangeKey;
                    return {
                        x: startOfDayTimestamp(start) - halfStrokeWidthMs,
                        x2: endOfDayTimestamp(end) - halfStrokeWidthMs,
                        fillColor: isNew ? "#FD808B" : "#B5B6E3",
                        opacity: isHovered ? 0.5 : 0.25,
                        borderColor: isHovered ? (isNew ? "#D6336C" : "#6C63B5") : "transparent",
                    };
                }),
            },
        }, false, true, true, false);
    }, [mergedIgnoredRanges, hoveredIgnoredRangeKey, chartViewVersion, chartData, demandData]);

    const applySelectionRange = useCallback((xaxis) => {
        if (!xaxis || xaxis.min == null || xaxis.max == null) return;
        const rangeStart = new Date(floorToDayTimestamp(xaxis.min));
        const rangeEnd = new Date(floorToDayTimestamp(xaxis.max));

        const datesInRange = [];
        for (let d = new Date(rangeStart); d <= rangeEnd; d.setDate(d.getDate() + 1)) {
            datesInRange.push(dateKeyFromDate(d));
        }

        if (datesInRange.length === 0) return;

        updatePendingIgnoredDates((current) => {
            const currentSet = new Set(current);
            const allAlreadyMarked = datesInRange.every((dateKey) => currentSet.has(dateKey));
            datesInRange.forEach((dateKey) => {
                if (allAlreadyMarked) {
                    currentSet.delete(dateKey);
                } else {
                    currentSet.add(dateKey);
                }
            });
            return Array.from(currentSet);
        });
    }, [updatePendingIgnoredDates]);

    const updateSelectionLabels = useCallback((drag, leftClientX, rightClientX) => {
        const wrapperEl = chartWrapperRef.current;
        const globals = chartInstanceRef.current && chartInstanceRef.current.w && chartInstanceRef.current.w.globals;
        const gridEl = wrapperEl && wrapperEl.querySelector(".apexcharts-grid");
        const gridRect = gridEl && gridEl.getBoundingClientRect();
        const resolve = (clientX) => (wrapperEl && globals && gridRect)
            ? resolveDayTimestamp(wrapperEl, globals, drag, gridRect, clientX)
            : dayTimestampFromClientX(drag, clientX);

        if (selectionStartLabelRef.current) {
            selectionStartLabelRef.current.textContent = formatSelectionDate(resolve(leftClientX));
        }
        if (selectionEndLabelRef.current) {
            selectionEndLabelRef.current.textContent = formatSelectionDate(resolve(rightClientX));
        }
    }, []);

    const handleSelectionMouseDown = (e) => {
        const wrapperEl = chartWrapperRef.current;
        const gridEl = wrapperEl && wrapperEl.querySelector(".apexcharts-grid");
        const globals = chartInstanceRef.current && chartInstanceRef.current.w && chartInstanceRef.current.w.globals;
        if (!wrapperEl || !gridEl || !globals) return;

        const wrapperRect = wrapperEl.getBoundingClientRect();
        const gridRect = gridEl.getBoundingClientRect();
        dragStateRef.current = {
            startClientX: e.clientX,
            wrapperLeft: wrapperRect.left,
            gridLeft: gridRect.left,
            gridWidth: gridRect.width,
            domainMin: globals.minX,
            domainMax: globals.maxX,
        };

        if (selectionRectElRef.current) {
            selectionRectElRef.current.style.display = "block";
            selectionRectElRef.current.style.left = `${e.clientX - wrapperRect.left}px`;
            selectionRectElRef.current.style.width = "0px";
        }
        hideHoverIndicator();
        updateSelectionLabels(dragStateRef.current, e.clientX, e.clientX);
    };

    const hideHoverIndicator = () => {
        if (hoverLabelElRef.current) hoverLabelElRef.current.style.display = "none";
        if (hoverLineElRef.current) hoverLineElRef.current.style.display = "none";
        hoveredIgnoredRangeRef.current = null;
        if (ignoredRangeRemoveBtnElRef.current) ignoredRangeRemoveBtnElRef.current.style.display = "none";
        if (ignoredRangeIndicatorElRef.current) ignoredRangeIndicatorElRef.current.style.display = "none";
        setHoveredIgnoredRangeKey((prev) => (prev === null ? prev : null));
    };

    const handleSelectionMouseMove = (e) => {
        if (dragStateRef.current || !hoverLabelElRef.current || !hoverLineElRef.current) return;

        const wrapperEl = chartWrapperRef.current;
        const gridEl = wrapperEl && wrapperEl.querySelector(".apexcharts-grid");
        const globals = chartInstanceRef.current && chartInstanceRef.current.w && chartInstanceRef.current.w.globals;
        if (!wrapperEl || !gridEl || !globals) return;

        const wrapperRect = wrapperEl.getBoundingClientRect();
        const gridRect = gridEl.getBoundingClientRect();
        const metrics = { gridLeft: gridRect.left, gridWidth: gridRect.width, domainMin: globals.minX, domainMax: globals.maxX };

        const ts = resolveDayTimestamp(wrapperEl, globals, metrics, gridRect, e.clientX);

        const left = `${e.clientX - wrapperRect.left}px`;
        hoverLabelElRef.current.style.display = "block";
        hoverLabelElRef.current.style.left = left;
        hoverLabelElRef.current.textContent = formatSelectionDate(ts);

        hoverLineElRef.current.style.display = "block";
        hoverLineElRef.current.style.left = left;

        const hoveredDateKey = dateKeyFromDate(new Date(ts));
        const hoveredRange = mergedIgnoredRanges.find(
            ({ start, end }) => hoveredDateKey >= start && hoveredDateKey <= end
        ) || null;
        hoveredIgnoredRangeRef.current = hoveredRange;

        const rangeKey = hoveredRange ? `${hoveredRange.start}_${hoveredRange.end}` : null;
        setHoveredIgnoredRangeKey((prev) => (prev === rangeKey ? prev : rangeKey));

        if (ignoredRangeRemoveBtnElRef.current) {
            if (!hoveredRange) {
                ignoredRangeRemoveBtnElRef.current.style.display = "none";
            } else {
                const pixelFromTs = (rangeTs) =>
                    metrics.gridLeft + ((rangeTs - metrics.domainMin) / (metrics.domainMax - metrics.domainMin)) * metrics.gridWidth - wrapperRect.left;
                const BTN_WIDTH = 18;
                const HALF_STROKE_WIDTH_PX = BAR_STROKE_WIDTH_PX / 2;
                const rangeLeftPxCalc = pixelFromTs(startOfDayTimestamp(hoveredRange.start)) - HALF_STROKE_WIDTH_PX;
                const rangeRightPxCalc = pixelFromTs(startOfDayTimestamp(hoveredRange.end) + ONE_DAY_MS) - HALF_STROKE_WIDTH_PX;
                // When zoomed way out, even a multi-day range can render narrower than the button
                // itself — flush-right-edge then makes it look like it's floating off to one side.
                // Center the button on the range in that case; a wide-enough range keeps the
                // flush-right placement, which reads as attached to it.
                const btnLeftPx = (rangeRightPxCalc - rangeLeftPxCalc) < BTN_WIDTH
                    ? (rangeLeftPxCalc + rangeRightPxCalc) / 2 - BTN_WIDTH / 2
                    : rangeRightPxCalc - BTN_WIDTH;
                ignoredRangeRemoveBtnElRef.current.style.display = "flex";
                ignoredRangeRemoveBtnElRef.current.style.left = `${btnLeftPx}px`;
            }
        }

        if (ignoredRangeIndicatorElRef.current) {
            if (!hoveredRange) {
                ignoredRangeIndicatorElRef.current.style.display = "none";
            } else {
                const pixelFromTs = (rangeTs) =>
                    metrics.gridLeft + ((rangeTs - metrics.domainMin) / (metrics.domainMax - metrics.domainMin)) * metrics.gridWidth - wrapperRect.left;
                const HALF_STROKE_WIDTH_PX = BAR_STROKE_WIDTH_PX / 2;
                const rangeLeftPx = pixelFromTs(startOfDayTimestamp(hoveredRange.start)) - HALF_STROKE_WIDTH_PX;
                const rangeRightPx = pixelFromTs(startOfDayTimestamp(hoveredRange.end) + ONE_DAY_MS) - HALF_STROKE_WIDTH_PX;

                ignoredRangeIndicatorElRef.current.style.display = "block";
                ignoredRangeIndicatorElRef.current.style.top = `${gridRect.top - wrapperRect.top}px`;
                ignoredRangeIndicatorElRef.current.style.left = `${rangeLeftPx}px`;
                ignoredRangeIndicatorElRef.current.style.width = `${Math.max(0, rangeRightPx - rangeLeftPx)}px`;

                if (ignoredRangeFromLabelElRef.current) {
                    ignoredRangeFromLabelElRef.current.textContent = formatSelectionDate(startOfDayTimestamp(hoveredRange.start));
                }
                if (ignoredRangeToLabelElRef.current) {
                    ignoredRangeToLabelElRef.current.textContent = formatSelectionDate(startOfDayTimestamp(hoveredRange.end));
                }
            }
        }
    };

    const handleRemoveIgnoredRange = (e) => {
        e.stopPropagation();
        e.preventDefault();
        const range = hoveredIgnoredRangeRef.current;
        if (!range) return;
        updatePendingIgnoredDates((current) => current.filter((d) => !(d >= range.start && d <= range.end)));
        hideHoverIndicator();
    };

    const handleSelectionMouseLeave = () => {
        hideHoverIndicator();
    };

    useEffect(() => {
        if (!isSelectionModeActive) {
            hideHoverIndicator();
        }
    }, [isSelectionModeActive]);

    useEffect(() => {
        if (_.isEmpty(demandData)) {
            setIsSelectionModeActive(false);
        }
    }, [demandData]);

    useEffect(() => {
        const handleMouseMove = (e) => {
            const drag = dragStateRef.current;
            if (!drag || !selectionRectElRef.current) return;
            const left = Math.min(drag.startClientX, e.clientX) - drag.wrapperLeft;
            const width = Math.abs(e.clientX - drag.startClientX);
            selectionRectElRef.current.style.left = `${Math.max(0, left)}px`;
            selectionRectElRef.current.style.width = `${width}px`;
            updateSelectionLabels(drag, Math.min(drag.startClientX, e.clientX), Math.max(drag.startClientX, e.clientX));
        };

        const handleMouseUp = (e) => {
            const drag = dragStateRef.current;
            dragStateRef.current = null;
            if (selectionRectElRef.current) selectionRectElRef.current.style.display = "none";
            if (!drag) return;

            const wrapperEl = chartWrapperRef.current;
            const globals = chartInstanceRef.current && chartInstanceRef.current.w && chartInstanceRef.current.w.globals;
            const gridEl = wrapperEl && wrapperEl.querySelector(".apexcharts-grid");
            const gridRect = gridEl && gridEl.getBoundingClientRect();
            const resolve = (clientX) => (wrapperEl && globals && gridRect)
                ? resolveDayTimestamp(wrapperEl, globals, drag, gridRect, clientX)
                : dayTimestampFromClientX(drag, clientX);

            const pixelStart = Math.min(drag.startClientX, e.clientX) - drag.gridLeft;
            const pixelEnd = Math.max(drag.startClientX, e.clientX) - drag.gridLeft;
            // A plain click (negligible movement) toggles just the single day under the cursor.
            if (Math.abs(pixelEnd - pixelStart) < 3) {
                const clickTs = resolve(e.clientX);
                applySelectionRange({ min: clickTs, max: clickTs });
                return;
            }

            const rangeStartTs = resolve(Math.min(drag.startClientX, e.clientX));
            const rangeEndTs = resolve(Math.max(drag.startClientX, e.clientX));

            applySelectionRange({ min: rangeStartTs, max: rangeEndTs });
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [applySelectionRange, updateSelectionLabels]);

    const chartOptions = useMemo(() => ({
        ...ANALYTICS_CHART_LINE_OPTIONS,
        ...{
            xaxis: {
                ...ANALYTICS_CHART_LINE_OPTIONS.xaxis,
                labels: {
                    ...(ANALYTICS_CHART_LINE_OPTIONS.xaxis && ANALYTICS_CHART_LINE_OPTIONS.xaxis.labels),
                    datetimeUTC: false,
                    format: "dd MMM",
                },
            },
            yaxis: {
                labels: {
                    formatter: function (val) {
                        return val.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        });
                    },
                },
            },
            plotOptions: {
                bar: {
                    // Bars touch each other so each one fills its whole day, edge to edge.
                    columnWidth: "100%",
                },
            },
            // Keep stroke.width non-zero (rather than 0) so a zero-quantity day still renders as a
            // thin visible line instead of disappearing entirely. ApexCharts insets each bar's
            // right edge by this width, so the ignored-range highlight compensates for it directly
            // (see BAR_STROKE_WIDTH_PX usage) instead of relying on the bar being unshrunk.
            stroke: { width: BAR_STROKE_WIDTH_PX },
            tooltip: {
                y: {
                    title: {
                        formatter: (seriesName) => {
                            if (seriesName === `${t("demand.forecast")} ${t("demand.forecast_length")}`) {
                                return t("demand.forecast");
                            }
                            return seriesName;
                        },
                    },
                    formatter: function (val) {
                        return val.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        });
                    },
                },
            },
            chart: {
                toolbar: {
                    export: {
                        csv: {
                            filename: t('demand.title') + ' ' + materialCodeSelected,
                            headerCategory: t("demand.date"),
                            dateFormatter(timestamp) {
                                const d = new Date(timestamp);
                                const mm = String(d.getMonth() + 1).padStart(2, "0");
                                const dd = String(d.getDate()).padStart(2, "0");
                                const yy = String(d.getFullYear()).slice(-2);
                                return `${dd}/${mm}/${yy}`;
                            },
                        },
                        png: {
                            filename: t('demand.title') + ' ' + materialCodeSelected,
                        },
                        svg: {
                            filename: t('demand.title') + ' ' + materialCodeSelected,
                        }
                    },
                },
                events: {
                    mounted: handleChartMounted,
                    // Zooming/panning/resizing changes the grid's pixel-per-ms ratio, which the
                    // stroke-width compensation on the ignored-range highlight depends on.
                    zoomed: bumpChartViewVersion,
                    scrolled: bumpChartViewVersion,
                    beforeResetZoom: bumpChartViewVersion,
                },
            },
        }
    }), [materialCodeSelected, t, handleChartMounted, bumpChartViewVersion])

    useEffect(() => {
        setMaterialList([])
        setMaterialCodeSelected("")
        const fetchData = async () => {
            try {
                setFetchingData(true)
                const workspaceMaterials = await getWorkspaceMaterialsList(workspaceSelected);
                setMaterialList(workspaceMaterials)
                setFetchingData(false)
            } catch (error) {
                setFetchingData(false)
                console.error(error);
            }
        };
        fetchData();
    }, [workspaceSelected]);

    useEffect(() => {
        const fetchInitialIgnoredDates = async () => {
            try {
                const ignoredDatesResponse = await getIgnoredDates(workspaceSelected);
                const responseMaterials = ignoredDatesResponse.materials || [];
                const expandMaterialDates = (material) =>
                    material.dates.flatMap(({ start, end }) =>
                        expandDateRangeToKeys(ddmmyyyyToIsoDateKey(start), ddmmyyyyToIsoDateKey(end))
                    );

                const seededMaterials = {};
                responseMaterials.forEach((material) => {
                    seededMaterials[material.location_code_concatenation] = expandMaterialDates(material);
                });
                setBackendIgnoredDatesByMaterial(seededMaterials);
                setPendingIgnoredDatesByMaterial(seededMaterials);
            } catch (error) {
                console.error(error);
            }
        };
        fetchInitialIgnoredDates();
    }, [workspaceSelected]);


    useEffect(() => {
        setDemandData([])
        setDemandDataForecast([])
        setEditingRangeKey(null)
        setCurrentCalculationResult(null)
        setCalculationPreviewResult(null)
        const chart = chartInstanceRef.current;
        if (chart) {
            chart.updateOptions({ annotations: { xaxis: [] } }, false, true, true, false);
        }
    }, [materialCodeSelected])

    useEffect(() => {
        if(demandData.length === 0) setFilterDemandData({});
        setFilterDemandData(
            {
                name: t("demand.quantity"),
                data: demandData.reduce((acc, item) => {
                    const [day, month, year] = item.date.split("/");
                    const d = new Date(year, month - 1, day);

                    if (d >= new Date(aggregateFromDate) && d <= new Date(aggregateToDate)) {
                        // Bars are centered on their x value; anchoring at noon (not midnight) so a
                        // full-width column spans exactly from that day's midnight to the next one.
                        acc.push({ x: d.getTime() + HALF_DAY_MS, y: item.quantity });

                    }
                    return acc;
                }, []),
            },
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aggregateFromDate, aggregateToDate, demandData])

    useEffect(() => {
        if(demandDataForecast.length === 0) setFilterDemandDataForecast({});
        setFilterDemandDataForecast(
            {
                name: `${t("demand.forecast")} ${t("demand.forecast_length")}`,
                data: demandDataForecast.reduce((acc, item) => {
                    const [day, month, year] = item.date.split("/");
                    const d = new Date(year, month - 1, day);

                    if (d >= new Date(aggregateFromDate) && d <= new Date(aggregateToDate)) {
                        // Bars are centered on their x value; anchoring at noon (not midnight) so a
                        // full-width column spans exactly from that day's midnight to the next one.
                        acc.push({ x: d.getTime() + HALF_DAY_MS, y: item.quantity });

                    }
                    return acc;
                }, []),
            },
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aggregateFromDate, aggregateToDate, demandDataForecast])

    const alignSeries = (seriesA, seriesB) => {
        const allTimestamps = Array.from(
            new Set([...seriesA.data.map(p => p.x), ...seriesB.data.map(p => p.x)])
        ).sort((a, b) => a - b);

        const fill = (series, timestamps) => {
            const map = new Map(series.data.map(p => [p.x, p.y]));
            return {
                ...series,
                data: timestamps.map(ts => ({
                    x: ts,
                    y: map.has(ts) ? map.get(ts) : null
                }))
            };
        };

        return [
            fill(seriesA, allTimestamps),
            fill(seriesB, allTimestamps),
        ];
    };


    useEffect(() => {
        const hasDemand = Array.isArray(filterDemandData.data);
        const hasForecast = includeForecast && Array.isArray(filterDemandDataForecast.data);

        if (!hasDemand) {
            setChartData([]);
        } else if (hasForecast) {
            setChartData(alignSeries(filterDemandData, filterDemandDataForecast));
        } else {
            setChartData([filterDemandData]);
        }
    }, [filterDemandData, filterDemandDataForecast, includeForecast])

    const handleWorkspaceSelectorChange = (e, selectedValue) => {
        setWorkspaceSelected(selectedValue.value)
    }

    const handleMaterialSelectorChange = (e, selectedValue) => {
        setMaterialCodeSelected(selectedValue.value)
    }

    const handleDateFromChange = (e) => {
        const value = e.target.value
        setAggregateFromDate(value);
        if (aggregateToDate && value > aggregateToDate) {
            setAggregateToDate("");
        }
    }

    const handleDateToChange = (e) => {
        const value = e.target.value
        if (aggregateFromDate && value < aggregateFromDate) {
            return;
        }
        setAggregateToDate(value);
    }

    const handleIncludeForecastChange = (e, data) => {
        setIncludeForecast(data.checked)
    }

    const handleShowDemandData = async () => {
        try {
            setFetchingData(true)

            const demandDataPromise = includeForecast
                ? Promise.all([
                    getDemandData(workspaceSelected, materialCodeSelected, false),
                    getDemandData(workspaceSelected, materialCodeSelected, true),
                ])
                : getDemandData(workspaceSelected, materialCodeSelected, false).then((data) => [data, []]);

            const [
                [responseDemandData, responseDemandDataForecast],
                baselineResult,
            ] = await Promise.all([
                demandDataPromise,
                getOrderCalculationPreview(workspaceSelected, materialCodeSelected),
            ]);

            setDemandData(responseDemandData)
            if (includeForecast) {
                setDemandDataForecast(responseDemandDataForecast)
                setAggregateToDate(get12MonthsAheadFormatted())
            } else {
                setDemandDataForecast([])
            }

            setCurrentCalculationResult(baselineResult);

            setFetchingData(false)
        }
        catch (error){
            setFetchingData(false)
            console.log(error)
        }
    }

    const isIgnoredDatesDirty = !sameDateSet(pendingIgnoredDates, backendIgnoredDates);
    const dirtyMaterialCodes = useMemo(() => {
        const allCodes = new Set([
            ...Object.keys(backendIgnoredDatesByMaterial),
            ...Object.keys(pendingIgnoredDatesByMaterial),
        ]);
        return Array.from(allCodes).filter((code) =>
            !sameDateSet(pendingIgnoredDatesByMaterial[code] || [], backendIgnoredDatesByMaterial[code] || [])
        );
    }, [backendIgnoredDatesByMaterial, pendingIgnoredDatesByMaterial]);

    const isAnyMaterialIgnoredDatesDirty = dirtyMaterialCodes.length > 0;

    useUnsavedChangesReporter(isAnyMaterialIgnoredDatesDirty);

    const materialCodeToLabel = useCallback((code) => {
        const option = materialOptions.find((materialOption) => materialOption.value === code);
        return option ? option.text : code;
    }, [materialOptions]);

    // "new": has unsaved changes and never had dates saved on the backend before.
    // "edited": had dates saved on the backend before, and now has further unsaved changes.
    // "saved": has dates saved on the backend, with no pending changes.
    const editedMaterialsList = useMemo(() => {
        const dirtySet = new Set(dirtyMaterialCodes);
        const allCodes = new Set([
            ...Object.keys(backendIgnoredDatesByMaterial),
            ...dirtyMaterialCodes,
        ]);
        return Array.from(allCodes)
            .filter((code) => dirtySet.has(code) || (backendIgnoredDatesByMaterial[code] || []).length > 0)
            .map((code) => {
                const hasBeenSaved = (backendIgnoredDatesByMaterial[code] || []).length > 0;
                const status = !dirtySet.has(code) ? "saved" : (hasBeenSaved ? "edited" : "new");
                return { code, label: materialCodeToLabel(code), status };
            })
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [dirtyMaterialCodes, backendIgnoredDatesByMaterial, materialCodeToLabel]);

    const handleSaveAllIgnoredDates = async () => {
        if (dirtyMaterialCodes.length === 0) return;
        setSavingIgnoredDates(true)
        const materials = dirtyMaterialCodes.map((code) => ({
            location_code_concatenation: code,
            dates: buildMergedDateRanges(pendingIgnoredDatesByMaterial[code] || []).map(({ start, end }) => ({
                start: isoDateKeyToDDMMYYYY(start),
                end: isoDateKeyToDDMMYYYY(end),
            })),
        }));
        const result = await patchIgnoredDates(workspaceSelected, materials);
        if (result) {
            setBackendIgnoredDatesByMaterial((prev) => {
                const next = { ...prev };
                dirtyMaterialCodes.forEach((code) => {
                    next[code] = pendingIgnoredDatesByMaterial[code] || [];
                });
                return next;
            });
        }
        setSavingIgnoredDates(false)
    }

    const handleDiscardIgnoredDatesChanges = () => {
        updatePendingIgnoredDates(backendIgnoredDates)
        setEditingRangeKey(null)
    }

    const handleCalculateOrderPreview = async () => {
        if (!materialCodeSelected) return;
        setLoadingCalculationPreview(true);
        const ignoredDatesOverride = mergedIgnoredRanges.map(({ start, end }) => ({
            start: isoDateKeyToDDMMYYYY(start),
            end: isoDateKeyToDDMMYYYY(end),
        }));
        const result = await getOrderCalculationPreview(workspaceSelected, materialCodeSelected, {
            ignored_dates: ignoredDatesOverride,
        });
        if (result) {
            setCalculationPreviewResult(result);
        }
        setLoadingCalculationPreview(false);
    };

    const toggleSelectedRangesSection = () => {
        setIsSelectedRangesSectionCollapsed((collapsed) => !collapsed)
    }

    const rangeKeyFor = (range) => `${range.start}_${range.end}`;

    const handleStartEditRange = (range) => {
        setEditingRangeKey(rangeKeyFor(range));
        setEditRangeFromDate(range.start);
        setEditRangeToDate(range.end);
    };

    const handleCancelEditRange = () => {
        setEditingRangeKey(null);
    };

    const isEditRangeValid = Boolean(editRangeFromDate) && Boolean(editRangeToDate) && editRangeFromDate <= editRangeToDate;

    const handleSaveEditedRange = (originalRange) => {
        if (!isEditRangeValid) return;
        updatePendingIgnoredDates((current) => {
            const withoutOriginal = current.filter((d) => !(d >= originalRange.start && d <= originalRange.end));
            const newRangeDates = expandDateRangeToKeys(editRangeFromDate, editRangeToDate);
            return Array.from(new Set([...withoutOriginal, ...newRangeDates]));
        });
        setEditingRangeKey(null);
    };

    const handleRemoveMergedRange = (range) => {
        updatePendingIgnoredDates((current) => current.filter((d) => !(d >= range.start && d <= range.end)));
        if (editingRangeKey === rangeKeyFor(range)) {
            setEditingRangeKey(null);
        }
    };

    const handleDownloadDemandData = async () => {
        if (fetchingData) return;
        setFetchingData(true);
        await getDemandDataDownload(workspaceSelected);
        setFetchingData(false);
    };
    return (
        <Segment basic>
            <Segment>
                <Grid stackable>
                <Grid.Row>
                    <Grid.Column>
                    <Segment>
                    <Grid stackable>
                        <Grid.Row>
                            <Grid.Column width={6}>
                                <Form.Field className='material-section' required>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between'
                                    }}>
                                        <label className="field-title">{t('demand.workspaces')}</label>
                                        <Popup content={t("demand.download")} 
                                            trigger={
                                                <label className={`download-button-div ${fetchingData ? 'download-button-div-disabled' : ''}`} onClick={handleDownloadDemandData}>
                                                <span className="download-button-label">
                                                    {t("demand.downloadButton")}
                                                </span>
                                                <span
                                                    style={{
                                                        flex: 1,
                                                        color: "#FFFFFF",
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                    }}
                                                    >
                                                        <Icon name="download" size="small" />
                                                    </span>
                                               </label>

                                            } 
                                        />
                                    </div>
                                    <Dropdown 
                                        placeholder={t("demand.itemDescription")}
                                        fluid
                                        value = {workspaceSelected}
                                        onChange={handleWorkspaceSelectorChange}
                                        disabled={fetchingData}
                                        search selection 
                                        options={mapCurrentUserToWorkspaceOptions(workspaces, currentUser)}
                                    />
                                </Form.Field>
                            </Grid.Column>
                            <Grid.Column width={6}>
                                <Form.Field className='material-section' required>
                                    <label className="field-title">{t('demand.materialTitle')}</label>
                                    <Dropdown 
                                        placeholder={t("demand.itemDescription")}
                                        fluid
                                        value = {materialCodeSelected}
                                        onChange={handleMaterialSelectorChange}
                                        disabled={fetchingData}
                                        search selection options={materialOptions}
                                    />
                                </Form.Field>
                            </Grid.Column>
                                <Grid.Column width={4} verticalAlign="bottom" className="filter-group apply-button-column" >
                                    <Form.Field className="include-ai-forecast">
                                        <Checkbox 
                                            toggle
                                            label={t("demand.includeForecast")}
                                            disabled={fetchingData || !materialCodeSelected}
                                            checked={includeForecast}
                                            onChange={handleIncludeForecastChange}
                                            className="include-nonbuffered-toggle"
                                        />
                                        <Popup content={t("demand.forecastInfo")} 
                                            trigger={
                                                <span
                                                style={{
                                                    marginLeft: -20,
                                                    marginBottom: "0.3rem",
                                                    backgroundColor: "#E0E1E2",
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    width: 15,
                                                    height: 15,
                                                    borderRadius: "50%",
                                                    zIndex: 100,
                                                }}
                                                >
                                                    <Icon name="info" size="tiny" />
                                                </span>
                                            } 
                                        />
                                    </Form.Field>
                                </Grid.Column>
                        </Grid.Row>
                        <Grid.Row className="no-p-top">
                            <Grid.Column width={6} className="filter-group">
                                <Form.Field>
                                    <label>{t("demand.fromDate")}</label>
                                    <Input 
                                        type="date"
                                        value={aggregateFromDate}
                                        onChange={handleDateFromChange}
                                        disabled={fetchingData}
                                        fluid
                                        className="date-input"
                                    />
                                </Form.Field>
                            </Grid.Column>
                            <Grid.Column width={6} className="filter-group">
                                <Form.Field>
                                    <label>{t("demand.toDate")}</label>
                                    <Input 
                                        type="date"
                                        value={aggregateToDate}
                                        onChange={handleDateToChange}
                                        disabled={fetchingData}
                                        fluid
                                        className="date-input"
                                    />
                                </Form.Field>
                            </Grid.Column>
                            <Grid.Column width={2} verticalAlign="bottom" className="filter-group apply-button-column">
                                <Button 
                                    primary
                                    fluid
                                    className="apply-button"
                                    onClick={handleShowDemandData}
                                    disabled={fetchingData || !materialCodeSelected}
                                >
                                    {t("demand.apply")}
                                </Button>
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <Loader active={fetchingData} size="big"/>
                        </Grid.Row>
                    </Grid>
                    </Segment>
                    </Grid.Column>
                </Grid.Row>
                </Grid>
            </Segment>
            <Grid.Row>
                <Grid.Column>
                    <Segment>
                        <div className="demand-selection-mode-row">
                            {!_.isEmpty(demandData) && mergedIgnoredRanges.length > 0 &&
                                <div className="demand-ignored-legend">
                                    <span className="demand-ignored-legend-item">
                                        <span className="demand-ignored-legend-dot demand-ignored-legend-dot-saved" />
                                        {t("demand.editedMaterialsSavedBadge")}
                                    </span>
                                    <span className="demand-ignored-legend-item">
                                        <span className="demand-ignored-legend-dot demand-ignored-legend-dot-new" />
                                        {t("demand.editedMaterialsNewBadge")}
                                    </span>
                                </div>
                            }
                            <Checkbox
                                toggle
                                label={t("demand.selectionModeToggle")}
                                checked={isSelectionModeActive}
                                onChange={(e, data) => setIsSelectionModeActive(data.checked)}
                                disabled={fetchingData || !materialCodeSelected || _.isEmpty(demandData)}
                                className="include-nonbuffered-toggle"
                            />
                        </div>
                        <div className="demand-chart-wrapper" ref={chartWrapperRef}>
                            <Chart
                                options={chartOptions}
                                series={chartData}
                                type="bar"
                                height={chartOneHeight}
                            />
                            <div
                                className="demand-selection-overlay"
                                style={{ pointerEvents: isSelectionModeActive ? "auto" : "none" }}
                                onMouseDown={handleSelectionMouseDown}
                                onMouseMove={handleSelectionMouseMove}
                                onMouseLeave={handleSelectionMouseLeave}
                            >
                                <div ref={selectionRectElRef} className="demand-selection-drag-rect">
                                    <span ref={selectionStartLabelRef} className="demand-selection-label demand-selection-label-start" />
                                    <span ref={selectionEndLabelRef} className="demand-selection-label demand-selection-label-end" />
                                </div>
                                <div ref={hoverLineElRef} className="demand-selection-hover-line" />
                                <span ref={hoverLabelElRef} className="demand-selection-label demand-selection-hover-label" />
                                <span
                                    ref={ignoredRangeRemoveBtnElRef}
                                    className="demand-ignored-range-remove-btn"
                                    onMouseDown={handleRemoveIgnoredRange}
                                >
                                    &times;
                                </span>
                                <div ref={ignoredRangeIndicatorElRef} className="demand-ignored-range-indicator">
                                    <span ref={ignoredRangeFromLabelElRef} className="demand-selection-label demand-ignored-range-label-start" />
                                    <span ref={ignoredRangeToLabelElRef} className="demand-selection-label demand-ignored-range-label-end" />
                                </div>
                            </div>
                        </div>
                        {/* <Grid.Row>
                            <Loader active={_.isEmpty(materialDataList)} size="big"/>
                        </Grid.Row> */}
                        {!_.isEmpty(demandData) &&
                            (materialCalculationParams && materialCalculationParams.length > 0 ?
                                <>
                                <Table definition className="demand-metrics-table">
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.HeaderCell />
                                            <Table.HeaderCell>{t("demand.currentValueLabel")}</Table.HeaderCell>
                                            <Table.HeaderCell>{t("demand.newValueLabel")}</Table.HeaderCell>
                                            <Table.HeaderCell>%</Table.HeaderCell>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {materialCalculationParams.map((param, index) => {
                                            const hasPreview = param.nextRaw !== null;
                                            const percentage = hasPreview && typeof param.currentRaw === "number" && param.currentRaw !== 0
                                                ? ((param.nextRaw - param.currentRaw) / param.currentRaw) * 100
                                                : null;
                                            return (
                                                <Table.Row key={param.label || index}>
                                                    <Table.Cell>{param.label}</Table.Cell>
                                                    <Table.Cell>{param.current}</Table.Cell>
                                                    <Table.Cell>{hasPreview ? param.next : "-"}</Table.Cell>
                                                    <Table.Cell
                                                        positive={percentage !== null && percentage > 0}
                                                        negative={percentage !== null && percentage < 0}
                                                    >
                                                        {percentage !== null ? `${percentage > 0 ? "+" : ""}${percentage.toFixed(0)}%` : "-"}
                                                    </Table.Cell>
                                                </Table.Row>
                                            );
                                        })}
                                    </Table.Body>
                                </Table>
                                <div className="demand-calculate-preview-row">
                                    <Button
                                        primary
                                        loading={loadingCalculationPreview}
                                        onClick={handleCalculateOrderPreview}
                                        disabled={fetchingData || loadingCalculationPreview || !materialCodeSelected}
                                    >
                                        {t("demand.calculateOrderPreview")}
                                    </Button>
                                    <Popup content={t("demand.calculatePreviewInfo")}
                                        trigger={
                                            <span className="demand-calculate-info-icon">
                                                <Icon name="info" size="tiny" />
                                            </span>
                                        }
                                    />
                                </div>
                                </>
                            :
                                <div className="demand-metrics-empty">
                                    {t("demand.calculationParamsEmpty")}
                                </div>
                            )
                        }
                        <Segment className={`filter-section ${isSelectedRangesSectionCollapsed ? 'filter-section-collapsed' : ''}`}>
                            <div className="filter-section-header" onClick={toggleSelectedRangesSection}>
                                <h4 className="filter-section-title">
                                    {t("demand.selectedRangesTitle")}
                                    <span className="filter-count">
                                        ({!_.isEmpty(demandData) ? mergedIgnoredRanges.length : 0})
                                    </span>
                                </h4>
                                <Button
                                    icon
                                    basic
                                    size="tiny"
                                    className="toggle-filter-button"
                                >
                                    <Icon name={isSelectedRangesSectionCollapsed ? "angle down" : "angle up"} />
                                </Button>
                            </div>
                            <div className="filter-section-content" style={{ display: isSelectedRangesSectionCollapsed ? 'none' : 'block' }}>
                                {(!_.isEmpty(demandData) ? mergedIgnoredRanges : []).length === 0 ?
                                    <div className="selected-ranges-empty">
                                        {t("demand.selectedRangesEmpty")}
                                    </div>
                                :
                                    (!_.isEmpty(demandData) ? mergedIgnoredRanges : []).map((range) => {
                                        const key = rangeKeyFor(range);
                                        const isEditing = editingRangeKey === key;
                                        return (
                                            <div key={key} className="selected-range-row">
                                                {isEditing ?
                                                    <>
                                                        <Input
                                                            type="date"
                                                            value={editRangeFromDate}
                                                            onChange={(e) => setEditRangeFromDate(e.target.value)}
                                                            className="date-input selected-range-edit-input"
                                                        />
                                                        <span className="selected-range-edit-separator">-</span>
                                                        <Input
                                                            type="date"
                                                            value={editRangeToDate}
                                                            onChange={(e) => setEditRangeToDate(e.target.value)}
                                                            className="date-input selected-range-edit-input"
                                                        />
                                                        <div className="selected-range-actions">
                                                            <Button
                                                                primary
                                                                size="tiny"
                                                                onClick={() => handleSaveEditedRange(range)}
                                                                disabled={!isEditRangeValid}
                                                            >
                                                                {t("demand.saveRangeEdit")}
                                                            </Button>
                                                            <Button
                                                                basic
                                                                size="tiny"
                                                                onClick={handleCancelEditRange}
                                                            >
                                                                {t("demand.cancelRangeEdit")}
                                                            </Button>
                                                        </div>
                                                    </>
                                                :
                                                    <>
                                                        <span className="selected-range-dates">
                                                            {formatSelectionDate(startOfDayTimestamp(range.start))} - {formatSelectionDate(startOfDayTimestamp(range.end))}
                                                        </span>
                                                        <div className="selected-range-trailing">
                                                            <div className="selected-range-badge-column">
                                                                <Label
                                                                    size="tiny"
                                                                    className={`selected-range-badge ${range.isNew ? 'selected-range-badge-new' : 'selected-range-badge-saved'}`}
                                                                >
                                                                    {range.isNew ? t("demand.editedMaterialsNewBadge") : t("demand.editedMaterialsSavedBadge")}
                                                                </Label>
                                                            </div>
                                                            <div className="selected-range-actions">
                                                                <Button
                                                                    icon
                                                                    basic
                                                                    size="tiny"
                                                                    onClick={() => handleStartEditRange(range)}
                                                                    disabled={fetchingData || savingIgnoredDates}
                                                                >
                                                                    <Icon name="pencil" />
                                                                </Button>
                                                                <Button
                                                                    icon
                                                                    basic
                                                                    size="tiny"
                                                                    onClick={() => handleRemoveMergedRange(range)}
                                                                    disabled={fetchingData || savingIgnoredDates}
                                                                >
                                                                    <Icon name="trash" />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </>
                                                }
                                            </div>
                                        );
                                    })
                                }
                            </div>
                        </Segment>
                        <div className="ignored-dates-controls">
                            <span className="ignored-dates-hint">
                                {t("demand.ignoredDatesHint")}
                            </span>
                            <div className="ignored-dates-actions">
                                <span className="ignored-dates-count">
                                    {t("demand.ignoredDatesCount", { count: !_.isEmpty(demandData) ? pendingIgnoredDates.length : 0 })}
                                </span>
                                <Button
                                    basic
                                    onClick={handleDiscardIgnoredDatesChanges}
                                    disabled={fetchingData || savingIgnoredDates || !isIgnoredDatesDirty || _.isEmpty(demandData)}
                                >
                                    {t("demand.discardIgnoredDatesChanges")}
                                </Button>
                            </div>
                        </div>
                    </Segment>
                </Grid.Column>
            </Grid.Row>
            <div className="edited-materials-summary-row">
                <Button
                    basic
                    onClick={() => setShowEditedMaterialsModal(true)}
                    disabled={editedMaterialsList.length === 0}
                >
                    {t("demand.editedMaterialsCount", { count: editedMaterialsList.length })}
                </Button>
                <Button
                    primary
                    loading={savingIgnoredDates}
                    onClick={handleSaveAllIgnoredDates}
                    disabled={fetchingData || savingIgnoredDates || !isAnyMaterialIgnoredDatesDirty}
                >
                    {t("demand.saveAllIgnoredDates")}
                </Button>
            </div>
            <Modal
                open={showEditedMaterialsModal}
                onClose={() => setShowEditedMaterialsModal(false)}
                size="tiny"
            >
                <Modal.Header>{t("demand.editedMaterialsModalTitle")}</Modal.Header>
                <Modal.Content scrolling>
                    <List divided relaxed>
                        {editedMaterialsList.map(({ code, label, status }) => (
                            <List.Item key={code} className="edited-material-list-item">
                                <span>{label}</span>
                                <Label
                                    as="a"
                                    size="tiny"
                                    color={status === "saved" ? "grey" : undefined}
                                    className={`edited-material-badge edited-material-badge-${status}`}
                                    onClick={() => {
                                        setMaterialCodeSelected(code);
                                        setShowEditedMaterialsModal(false);
                                    }}
                                >
                                    {t(`demand.editedMaterials${status.charAt(0).toUpperCase()}${status.slice(1)}Badge`)}
                                </Label>
                            </List.Item>
                        ))}
                    </List>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => setShowEditedMaterialsModal(false)}>
                        {t("demand.closeModal")}
                    </Button>
                </Modal.Actions>
            </Modal>
        </Segment>
    )
};

Demand.propTypes = {
    currentWorkspaceId: PropTypes.number,
    currentUser: PropTypes.object.isRequired,
    workspaces: PropTypes.arrayOf(PropTypes.object),
    materialDataList: PropTypes.array,
    chartOneHeight: PropTypes.number,
    savedAggregateFromDate: PropTypes.string,
    savedAggregateToDate: PropTypes.string,
    workspaceLevelSettings: PropTypes.object,
};

const mapStateToProps = (state) => ({
    currentWorkspaceId: getSafeSelectedWorkspace(state),
    currentUser: state.auth.user,
    workspaceLevelSettings: state.adminConsole.workspaceLevelSettings,
    workspaces: state.workspace.workspaces,
    materialDataList: state.analytics.materialDataList,
    chartOneHeight: state.analytics.chartOneHeight,
    savedAggregateFromDate: state.analytics.savedAggregateFromDate,
    savedAggregateToDate: state.analytics.savedAggregateToDate,
});
export default connect(mapStateToProps)(Demand);